import React, { useState, useMemo } from 'react';
import { Vessel, ImportRecord, BitumenGrade } from '../../types';
import { 
  Search, Ship, FileSpreadsheet, ArrowUpDown, Filter, Package, 
  Users, List, Sigma, UserCircle, X, ExternalLink, FileText, 
  Calendar, Building2, Globe, Hash, Info, Layers, MapPin, TrendingUp,
  ArrowUp, ArrowDown
} from 'lucide-react';

interface ConsolidatedViewProps {
  vessels: Vessel[];
}

type GroupingMode = 'SUPPLIER' | 'IMPORTER';

interface Aggregate {
  identity: string;
  totalQuantity: number;
  vessels: Set<string>;
  partners: Set<string>; 
  grades: Set<string>;
  recordCount: number;
  latestArrival: string;
}

// Helper to unify grade display by removing 'Bitumen', 'Grade', etc.
const normalizeGrade = (grade: string) => {
    if (!grade) return 'UNKNOWN';
    return grade.toUpperCase()
        .replace(/BITUMEN/g, '')
        .replace(/GRADE/g, '')
        .replace(/FRADE/g, '')
        .trim();
};

interface DetailPopupProps {
  item: any;
  mode: GroupingMode;
  onClose: () => void;
}

const DetailPopup: React.FC<DetailPopupProps> = ({ item, mode, onClose }) => {
  if (!item) return null;

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md animate-in fade-in duration-300">
      <div className="bg-white rounded-[40px] shadow-2xl w-full max-w-2xl overflow-hidden border border-slate-200 animate-in zoom-in-95 duration-300 flex flex-col max-h-[85vh]">
        <header className={`p-8 ${mode === 'IMPORTER' ? 'bg-teal-600' : 'bg-indigo-600'} text-white flex justify-between items-center shrink-0`}>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-md">
              {mode === 'IMPORTER' ? <UserCircle size={24} /> : <Users size={24} />}
            </div>
            <div>
              <h3 className="text-xl font-black uppercase tracking-tight">Corporate Portfolio</h3>
              <p className="text-white/70 text-[10px] font-black uppercase tracking-widest mt-1">Intelligence Registry Node</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-xl transition-colors">
            <X size={24} />
          </button>
        </header>

        <div className="p-8 overflow-y-auto custom-scrollbar space-y-8 bg-slate-50/30">
          <div className="space-y-6">
            <div className="flex items-center gap-5 bg-white p-8 rounded-[32px] border border-slate-200 shadow-sm relative overflow-hidden">
              <div className={`w-16 h-16 rounded-[1.25rem] flex items-center justify-center ${mode === 'IMPORTER' ? 'bg-teal-50 text-teal-600' : 'bg-indigo-50 text-indigo-600'}`}>
                {mode === 'IMPORTER' ? <UserCircle size={32} /> : <Users size={32} />}
              </div>
              <div className="relative z-10">
                <h4 className="text-2xl font-black text-slate-800 uppercase tracking-tight leading-none">{item.identity}</h4>
                <p className="text-xs font-bold text-slate-400 uppercase tracking-[0.2em] mt-2">Active {mode === 'IMPORTER' ? 'Client' : 'Supply Partner'}</p>
              </div>
              <div className="absolute top-0 right-0 p-4 opacity-5">
                 <Globe size={100} />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white p-5 rounded-3xl border border-slate-200 shadow-sm text-center">
                 <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Lifetime Volume</p>
                 <p className="text-xl font-black text-indigo-600">{item.totalQuantity.toLocaleString()}</p>
                 <p className="text-[8px] font-black text-slate-300 uppercase">Metric Tons</p>
              </div>
              <div className="bg-white p-5 rounded-3xl border border-slate-200 shadow-sm text-center">
                 <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Record Count</p>
                 <p className="text-xl font-black text-slate-800">{item.recordCount}</p>
                 <p className="text-[8px] font-black text-slate-300 uppercase">Entries Found</p>
              </div>
              <div className="bg-white p-5 rounded-3xl border border-slate-200 shadow-sm text-center">
                 <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Vessel Range</p>
                 <p className="text-xl font-black text-slate-800">{item.vessels.size}</p>
                 <p className="text-[8px] font-black text-slate-300 uppercase">Unique Carriers</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-[2.5rem] border border-slate-200 shadow-sm">
                 <h5 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                   <Ship size={14} className="text-indigo-400" /> Carrier Network
                 </h5>
                 <div className="flex flex-wrap gap-2">
                    {Array.from(item.vessels).map((v: any) => (
                      <span key={v} className="px-3 py-1.5 bg-slate-50 border border-slate-100 rounded-xl text-[10px] font-black text-slate-600 uppercase">
                        {v}
                      </span>
                    ))}
                 </div>
              </div>
              <div className="bg-white p-6 rounded-[2.5rem] border border-slate-200 shadow-sm">
                 <h5 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                   <Layers size={14} className="text-teal-400" /> Grade Variations
                 </h5>
                 <div className="flex flex-wrap gap-2">
                    {Array.from(item.grades).map((g: any) => (
                      <span key={g} className="px-3 py-1.5 bg-teal-50 border border-teal-100 rounded-xl text-[10px] font-black text-teal-700 uppercase">
                        {normalizeGrade(g)}
                      </span>
                    ))}
                 </div>
              </div>
              <div className="bg-white p-6 rounded-[2.5rem] border border-slate-200 shadow-sm md:col-span-2">
                 <h5 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                   <Globe size={14} className="text-indigo-400" /> Suppliers
                 </h5>
                 <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {Array.from(item.partners).map((p: any) => (
                      <div key={p} className="px-3 py-2 bg-slate-50 border border-slate-100 rounded-xl text-[9px] font-bold text-slate-500 uppercase truncate">
                        {p}
                      </div>
                    ))}
                 </div>
              </div>
            </div>
          </div>
        </div>

        <footer className="p-8 bg-slate-50 border-t border-slate-100 flex justify-between items-center shrink-0">
           <div className="flex items-center gap-2">
              <TrendingUp size={14} className="text-emerald-500" />
              <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Nexus Intelligence Handshake Active</span>
           </div>
           <button 
              onClick={onClose}
              className="px-10 py-3 bg-slate-900 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-slate-800 transition-all active:scale-95 shadow-xl"
           >
              Done
           </button>
        </footer>
      </div>
    </div>
  );
};

export const ConsolidatedView: React.FC<ConsolidatedViewProps> = ({ vessels }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [groupingMode, setGroupingMode] = useState<GroupingMode>('IMPORTER');
  const [sortConfig, setSortConfig] = useState<{ key: 'identity' | 'totalQuantity'; direction: 'asc' | 'desc' }>({
    key: 'totalQuantity',
    direction: 'desc'
  });
  const [selectedDetail, setSelectedDetail] = useState<any | null>(null);

  const allConsignments = useMemo(() => {
    const flat: ImportRecord[] = [];
    vessels.forEach(v => {
      if (v.manifest && Array.isArray(v.manifest)) {
        v.manifest.forEach(m => {
          flat.push({ ...m, vesselName: v.name }); 
        });
      }
    });
    return flat;
  }, [vessels]);

  const aggregates = useMemo(() => {
    const map = new Map<string, Aggregate>();
    const identityField = groupingMode === 'SUPPLIER' ? 'supplier' : 'importerName';
    const partnerField = groupingMode === 'SUPPLIER' ? 'importerName' : 'supplier';

    allConsignments.forEach(item => {
      const id = item[identityField] || 'UNKNOWN';
      if (!map.has(id)) {
        map.set(id, {
          identity: id,
          totalQuantity: 0,
          vessels: new Set(),
          partners: new Set(),
          grades: new Set(),
          recordCount: 0,
          latestArrival: item.arrivalDate
        });
      }

      const agg = map.get(id)!;
      agg.totalQuantity += (item.quantity || 0);
      agg.vessels.add(item.vesselName);
      agg.partners.add(item[partnerField] || 'UNKNOWN');
      agg.grades.add(normalizeGrade(item.bitumenGrade));
      agg.recordCount += 1;
      
      if (new Date(item.arrivalDate) > new Date(agg.latestArrival)) {
        agg.latestArrival = item.arrivalDate;
      }
    });

    return Array.from(map.values());
  }, [allConsignments, groupingMode]);

  const handleSort = (key: 'identity' | 'totalQuantity') => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const filteredData = useMemo(() => {
    return aggregates
      .filter(item => item.identity.toLowerCase().includes(searchTerm.toLowerCase()))
      .sort((a, b) => {
        const { key, direction } = sortConfig;
        if (key === 'totalQuantity') {
          return direction === 'asc' ? a.totalQuantity - b.totalQuantity : b.totalQuantity - a.totalQuantity;
        } else {
          return direction === 'asc' 
            ? a.identity.localeCompare(b.identity) 
            : b.identity.localeCompare(a.identity);
        }
      });
  }, [aggregates, searchTerm, sortConfig]);

  const totals = useMemo(() => ({
    volume: allConsignments.reduce((sum, item) => sum + (item.quantity || 0), 0),
    count: allConsignments.length,
    entityCount: aggregates.length
  }), [allConsignments, aggregates]);

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <DetailPopup 
        item={selectedDetail} 
        mode={groupingMode} 
        onClose={() => setSelectedDetail(null)} 
      />

      <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-6">
        <div>
          <h2 className="text-2xl font-black text-slate-800 uppercase tracking-tight">Consolidated Analytics</h2>
          <p className="text-slate-500 text-sm font-medium">Strategic commercial aggregations across the branch port registry.</p>
        </div>
        
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full xl:w-auto">
          <div className="bg-slate-100 p-1 rounded-2xl flex border border-slate-200 shadow-inner">
            <button 
              onClick={() => setGroupingMode('IMPORTER')}
              className={`flex items-center gap-2 px-6 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${groupingMode === 'IMPORTER' ? 'bg-white text-teal-600 shadow-md' : 'text-slate-400 hover:text-slate-600'}`}
            >
              <UserCircle size={14} /> Importers/Consignees
            </button>
            <button 
              onClick={() => setGroupingMode('SUPPLIER')}
              className={`flex items-center gap-2 px-6 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${groupingMode === 'SUPPLIER' ? 'bg-white text-indigo-600 shadow-md' : 'text-slate-400 hover:text-slate-600'}`}
            >
              <Sigma size={14} /> Suppliers
            </button>
          </div>

          <div className="relative flex-1 sm:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input 
              type="text" 
              placeholder={`Search ${groupingMode.toLowerCase()}s...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-2xl shadow-sm focus:ring-4 focus:ring-indigo-500/10 outline-none transition-all text-sm"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white p-5 rounded-3xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow cursor-default group">
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 group-hover:text-indigo-500 transition-colors">Total Registry Payload</p>
              <p className="text-2xl font-black text-indigo-600">{totals.volume.toLocaleString()} <span className="text-xs text-slate-400">MT</span></p>
          </div>
          <div className="bg-white p-5 rounded-3xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow cursor-default group">
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 group-hover:text-indigo-500 transition-colors">Strategic Entities</p>
              <p className="text-2xl font-black text-slate-900">{totals.entityCount}</p>
          </div>
      </div>

      <div className="bg-white rounded-[40px] border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-[10px] font-black text-slate-400 uppercase tracking-widest">
              <tr>
                <th 
                  className="px-8 py-5 text-left cursor-pointer hover:text-indigo-600 transition-colors group/header"
                  onClick={() => handleSort('identity')}
                >
                  <div className="flex items-center gap-2">
                    {groupingMode === 'SUPPLIER' ? 'Supplier Name' : 'Importer/Consignee Identity'}
                    {sortConfig.key === 'identity' ? (
                      sortConfig.direction === 'asc' ? <ArrowUp size={14} className="text-indigo-500" /> : <ArrowDown size={14} className="text-indigo-500" />
                    ) : (
                      <ArrowUpDown size={14} className="text-slate-300 opacity-0 group-hover/header:opacity-100 transition-opacity" />
                    )}
                  </div>
                </th>
                <th className="px-8 py-5 text-left">Portfolio Grades</th>
                <th className="px-8 py-5 text-left">Partners</th>
                <th className="px-8 py-5 text-center">BL's</th>
                <th 
                  className="px-8 py-5 text-right cursor-pointer hover:text-indigo-600 transition-colors group/header" 
                  onClick={() => handleSort('totalQuantity')}
                >
                  <div className="flex items-center justify-end gap-2">
                    Total Tonnage
                    {sortConfig.key === 'totalQuantity' ? (
                      sortConfig.direction === 'asc' ? <ArrowUp size={14} className="text-indigo-500" /> : <ArrowDown size={14} className="text-indigo-500" />
                    ) : (
                      <ArrowUpDown size={14} className="text-slate-300 opacity-0 group-hover/header:opacity-100 transition-opacity" />
                    )}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {(filteredData as Aggregate[]).map((agg, idx) => (
                <tr 
                  key={idx} 
                  onClick={() => setSelectedDetail(agg)}
                  className="hover:bg-indigo-50/50 transition-colors cursor-pointer group"
                >
                  <td className="px-8 py-5">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-2xl flex items-center justify-center ${groupingMode === 'SUPPLIER' ? 'bg-indigo-50 text-indigo-500' : 'bg-teal-50 text-teal-500'} group-hover:scale-110 transition-transform`}>
                        {groupingMode === 'SUPPLIER' ? <Users size={20} /> : <UserCircle size={20} />}
                      </div>
                      <div>
                        <p className="font-black text-slate-800 uppercase tracking-tight group-hover:text-indigo-600 transition-colors">{agg.identity}</p>
                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-0.5">Vessels: {agg.vessels.size}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-8 py-5">
                    <div className="flex flex-wrap gap-1 max-w-[250px]">
                      {Array.from(agg.grades).map(g => (
                        <span key={g} className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[9px] font-black uppercase border border-slate-200">{normalizeGrade(g)}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-8 py-5">
                    <div className="text-slate-500 text-[10px] font-bold uppercase leading-tight max-w-[200px] truncate" title={Array.from(agg.partners).join(', ')}>
                      {Array.from(agg.partners).join(', ')}
                    </div>
                  </td>
                  <td className="px-8 py-5 text-center">
                     <span className="bg-slate-50 border border-slate-100 px-3 py-1 rounded-full text-xs font-black text-slate-700">
                       {agg.recordCount}
                     </span>
                  </td>
                  <td className="px-8 py-5 text-right">
                     <p className={`text-lg font-black ${groupingMode === 'SUPPLIER' ? 'text-indigo-600' : 'text-teal-600'}`}>{agg.totalQuantity.toLocaleString()} <span className="text-xs text-slate-400 font-bold">MT</span></p>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ConsolidatedView;